import React, { useState } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Badge,
  Input,
  FormGroup,
  Label,
} from "reactstrap";
import { getActiveColorStyle } from "../../utils/colorUtils";

const DealsQueues = ({ activeColor = "blue" }) => {
  const [selectedQueue, setSelectedQueue] = useState("pending");
  const [isLoading, setIsLoading] = useState(false);
  
  // Sample queue data
  const [queues] = useState({
    pending: [
      {
        id: 1,
        dealNumber: "DEAL-001",
        customerName: "John Smith",
        vehicle: "2020 Toyota Camry",
        dealType: "Retail",
        amount: "$25,000",
        status: "Pending Approval",
        date: "2024-01-15",
        priority: "High"
      },
      {
        id: 2,
        dealNumber: "DEAL-003",
        customerName: "Mike Johnson",
        vehicle: "2021 Ford F-150",
        dealType: "Retail",
        amount: "$45,000",
        status: "Pending Finance",
        date: "2024-01-20",
        priority: "Medium"
      }
    ],
    approved: [
      {
        id: 3,
        dealNumber: "DEAL-002",
        customerName: "Jane Doe",
        vehicle: "2019 Honda Civic",
        dealType: "Wholesale",
        amount: "$18,500",
        status: "Approved",
        date: "2024-01-10",
        priority: "Low"
      }
    ],
    completed: [
      {
        id: 4,
        dealNumber: "DEAL-004",
        customerName: "Emily Davis",
        vehicle: "2018 BMW X5",
        dealType: "Retail",
        amount: "$38,000",
        status: "Completed",
        date: "2024-01-18",
        priority: "Low"
      }
    ]
  });

  // Get active color style
  const activeColorStyle = getActiveColorStyle(activeColor);

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'Pending Approval': return 'warning';
      case 'Pending Finance': return 'info';
      case 'Approved': return 'success';
      case 'Completed': return 'primary';
      default: return 'secondary';
    }
  };

  const getPriorityBadgeColor = (priority) => {
    switch (priority) {
      case 'High': return 'danger';
      case 'Medium': return 'warning';
      case 'Low': return 'success';
      default: return 'secondary';
    }
  };

  const getDealTypeBadgeColor = (type) => {
    return type === 'Retail' ? 'info' : 'warning';
  };

  const currentQueue = queues[selectedQueue] || [];

  return (
    <div className="content">
      <Row>
        <Col md="12">
          <Card>
            <CardHeader>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <CardTitle tag="h4" style={{ margin: 0 }}>Deals Queues</CardTitle>
                <div>
                  <Button
                    size="sm"
                    style={{ ...activeColorStyle, color: 'white', marginRight: '10px' }}
                  >
                    Export
                  </Button>
                  <Button
                    size="sm"
                    color="success"
                  >
                    New Deal
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardBody>
              {/* Queue Selection */}
              <Row className="mb-4">
                <Col md="12">
                  <div className="d-flex">
                    <Button
                      color={selectedQueue === "pending" ? "warning" : "secondary"}
                      className="mr-2"
                      onClick={() => setSelectedQueue("pending")}
                    >
                      Pending ({queues.pending.length})
                    </Button>
                    <Button
                      color={selectedQueue === "approved" ? "success" : "secondary"}
                      className="mr-2"
                      onClick={() => setSelectedQueue("approved")}
                    >
                      Approved ({queues.approved.length})
                    </Button>
                    <Button
                      color={selectedQueue === "completed" ? "primary" : "secondary"}
                      onClick={() => setSelectedQueue("completed")}
                    >
                      Completed ({queues.completed.length})
                    </Button>
                  </div>
                </Col>
              </Row>

              {/* Queue Statistics */}
              <Row className="mb-4">
                <Col md="12">
                  <div className="d-flex">
                    <Badge color="warning" className="mr-2">
                      Pending: {queues.pending.length}
                    </Badge>
                    <Badge color="success" className="mr-2">
                      Approved: {queues.approved.length}
                    </Badge>
                    <Badge color="primary" className="mr-2">
                      Completed: {queues.completed.length}
                    </Badge>
                    <Badge color="info">
                      Total: {queues.pending.length + queues.approved.length + queues.completed.length}
                    </Badge>
                  </div>
                </Col>
              </Row>

              {/* Deals Table */}
              {isLoading ? (
                <div className="text-center">
                  <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : (
                <Table responsive>
                  <thead>
                    <tr>
                      <th>Deal Number</th>
                      <th>Customer Name</th>
                      <th>Vehicle</th>
                      <th>Deal Type</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Priority</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentQueue.map((deal) => (
                      <tr key={deal.id}>
                        <td>
                          <strong>{deal.dealNumber}</strong>
                        </td>
                        <td>{deal.customerName}</td>
                        <td>{deal.vehicle}</td>
                        <td>
                          <Badge color={getDealTypeBadgeColor(deal.dealType)}>
                            {deal.dealType}
                          </Badge>
                        </td>
                        <td>
                          <strong>{deal.amount}</strong>
                        </td>
                        <td>
                          <Badge color={getStatusBadgeColor(deal.status)}>
                            {deal.status}
                          </Badge>
                        </td>
                        <td>
                          <Badge color={getPriorityBadgeColor(deal.priority)}>
                            {deal.priority}
                          </Badge>
                        </td>
                        <td>{deal.date}</td>
                        <td>
                          <Button size="sm" color="info" style={{ marginRight: '5px' }}>
                            View
                          </Button>
                          <Button size="sm" color="warning" style={{ marginRight: '5px' }}>
                            Edit
                          </Button>
                          {selectedQueue === "pending" && (
                            <Button size="sm" color="success">
                              Approve
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}

              {currentQueue.length === 0 && (
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'center', 
                  alignItems: 'center', 
                  minHeight: '100px',
                  color: '#6c757d',
                  fontSize: '16px'
                }}>
                  No deals found in this queue.
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default DealsQueues; 